<template>
  <div v-if="visible" class="ai-panel-overlay" @click.self="$emit('close')">
    <div class="ai-panel-content slide-in-right" :class="{ dark: isDarkMode }">
      <div class="ai-panel-header">
        <h2><i class="fas fa-comments"></i> {{ $t('Ask AI') }}</h2>
        <button class="ai-close" @click="$emit('close')"><i class="fas fa-times"></i></button>
      </div>

      <!-- Page-aware context banner (multi-agent) -->
      <div v-if="pageContext" class="ai-context-banner" :class="pageContext.pageType">
        <i :class="pageContextIcon"></i>
        <span class="ai-context-label">{{ $t('You are on') }} <strong>{{ pageContext.label }}</strong></span>
        <span v-if="isPageHelp" class="ai-context-badge">{{ $t('Page help') }}</span>
        <span v-else-if="isTranscriptAgent && elementId" class="ai-context-badge ok">{{ fileName || elementId }}</span>
        <span v-else-if="isTranscriptAgent && !elementId" class="ai-context-badge warn">{{ $t('No file selected') }}</span>
      </div>
      <p v-if="isPageHelp" class="ai-hint">{{ $t('Ask about how to use this page — the assistant will not search transcripts in this mode.') }}</p>
      <p v-else-if="!supportsTranscript && scope === 'single'" class="ai-hint ai-hint-warn">{{ $t('This page has no transcript context — switched to global search.') }}</p>

      <!-- Agent tabs (multi-agent) -->
      <div v-if="pageContext" class="ai-agent-tabs">
        <button
          v-if="supportsTranscript"
          type="button"
          class="ai-agent-tab"
          :class="{ active: effectiveAgent === 'transcript' }"
          :disabled="!elementId"
          :title="!elementId ? $t('Open a transcript first') : ''"
          @click="switchAgent('transcript')"
        ><i class="fas fa-file-lines"></i> {{ $t('This file') }}</button>
        <button type="button" class="ai-agent-tab" :class="{ active: effectiveAgent === 'global' }" @click="switchAgent('global')">
          <i class="fas fa-layer-group"></i> {{ $t('All transcripts') }}
        </button>
        <button v-if="!supportsTranscript" type="button" class="ai-agent-tab" :class="{ active: effectiveAgent === 'page-help' }" @click="switchAgent('page-help')">
          <i class="fas fa-circle-question"></i> {{ $t('Page help') }}
        </button>
      </div>

      <!-- Scope toggle (kept for transcript/global agents) -->
      <div v-if="!isPageHelp" class="ai-section">
        <label class="ai-label">{{ $t('Scope') }}</label>
        <div class="ai-scope-toggle">
          <button
            type="button"
            class="ai-scope-btn"
            :class="{ active: scope === 'single' }"
            :disabled="!elementId || !supportsTranscript"
            :title="!supportsTranscript ? $t('Not available on this page') : (!elementId ? $t('Select a transcript first') : '')"
            @click="scope = 'single'"
          >
            <i class="fas fa-file-lines"></i> {{ $t('This transcript') }}
          </button>
          <button
            type="button"
            class="ai-scope-btn"
            :class="{ active: scope === 'global' }"
            @click="scope = 'global'"
          >
            <i class="fas fa-layer-group"></i> {{ $t('All transcripts') }}
          </button>
        </div>
        <div v-if="scope === 'single' && elementId && supportsTranscript" class="ai-source-chip">
          <i class="fas fa-file-lines"></i>
          <span class="ai-source-name">{{ fileName || elementId }}</span>
        </div>
        <p v-if="scope === 'global' && !isPageHelp" class="ai-hint">{{ $t('AI will search your entire indexed library. It can re-search if needed.') }}</p>
        <p v-else-if="!elementId && scope === 'single'" class="ai-hint ai-hint-warn">{{ $t('No transcript selected — switching to global search.') }}</p>
      </div>

      <!-- Conversation history -->
      <div v-if="history.length" class="ai-conversation">
        <div v-for="(msg, idx) in history" :key="idx" class="ai-msg" :class="msg.role">
          <div class="ai-msg-role">
            <i :class="msg.role === 'user' ? 'fas fa-user' : 'fas fa-wand-magic-sparkles'"></i>
            {{ msg.role === 'user' ? $t('You') : 'AI' }}
          </div>
          <div class="ai-msg-body">{{ msg.content }}</div>
        </div>
      </div>

      <!-- Current answer -->
      <div v-if="answer" class="ai-section ai-result">
        <div class="ai-result-header">
          <label class="ai-label">{{ $t('Answer') }}</label>
          <button class="btn btn-sm btn-neutral" @click="copyAnswer"><i class="fas fa-copy"></i> {{ $t('Copy') }}</button>
        </div>
        <div class="ai-result-body">{{ answer }}</div>
        <div v-if="citations.length" class="ai-citations">
          <div class="ai-citations-title"><i class="fas fa-link"></i> {{ $t('Sources') }}</div>
          <button
            v-for="(c, i) in citations"
            :key="i"
            type="button"
            class="ai-citation"
            @click="openCitation(c)"
          >
            <span class="ai-citation-file">{{ c.fileName }}</span>
            <span v-if="c.start != null" class="ai-citation-time">{{ formatTime(c.start) }}</span>
            <span class="ai-citation-snippet">“{{ c.snippet }}”</span>
          </button>
        </div>
        <div v-if="noResults" class="ai-hint">{{ $t('No relevant content found. Try rephrasing or index more files.') }}</div>
      </div>

      <div v-if="error" class="ai-status ai-status-error">{{ error }}</div>
      <div v-if="loading" class="ai-status ai-status-progress">
        <i class="fas fa-spinner fa-spin"></i> {{ $t('Thinking...') }} <span class="ai-hint">{{ $t('AI may search your index again if needed') }}</span>
      </div>

      <!-- 2C Preset FAB chips (gated by supportsTranscriptContext) -->
      <div v-if="supportsTranscript" class="ai-preset-row">
        <span class="ai-preset-label">{{ $t('Quick prompts') }}</span>
        <div class="ai-presets ai-presets-row">
          <button
            type="button"
            class="ai-chip ai-preset-chip"
            :disabled="loading"
            @click="applyPreset('explain-bill')"
          >{{ $t('aiTools.presets.explainBill') }}</button>
          <button
            type="button"
            class="ai-chip ai-preset-chip"
            :disabled="loading"
            @click="applyPreset('action-items')"
          >{{ $t('aiTools.presets.actionItems') }}</button>
          <button
            type="button"
            class="ai-chip ai-preset-chip"
            :disabled="loading"
            @click="applyPreset('find-receipts')"
          >{{ $t('aiTools.presets.findReceipts') }}</button>
        </div>
      </div>

      <!-- Question input -->
      <div class="ai-section ai-question-row">
        <textarea
          v-model="question"
          class="ai-textarea"
          rows="3"
          :placeholder="isPageHelp ? $t('Ask about this page...') : (scope === 'single' ? $t('Ask about this transcript...') : $t('Ask about all your transcripts...'))"
          :disabled="loading"
          @keydown.enter.exact.prevent="doAsk"
          @keydown.enter.shift.exact.stop
        ></textarea>
        <div class="ai-question-actions">
          <span class="ai-hint">Shift+Enter {{ $t('for new line') }} · Enter {{ $t('to send') }}</span>
          <button class="btn btn-fill btn-info" :disabled="loading || !canAsk" @click="doAsk">
            <i class="fas fa-paper-plane"></i> {{ $t('Ask') }}
          </button>
        </div>
      </div>

      <div class="ai-actions">
        <button class="btn btn-cancel" @click="$emit('close')">{{ $t('Close') }}</button>
        <button v-if="history.length" class="btn btn-neutral btn-sm" @click="clearHistory">{{ $t('Clear chat') }}</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useThemeStore } from '@stores/themeStore';
import { ask, formatTime, type Citation } from '@utils/aiTools';
import { AI_PRESETS, type AiPreset } from '@stores/aiToolsStore';
import { notify } from '@components/NotificationPlugin';

export default defineComponent({
  name: 'AiAskPanel',
  props: {
    visible: { type: Boolean, default: false },
    elementId: { type: String, default: '' },
    fileName: { type: String, default: '' },
    projectId: { type: String, default: '' },
    outputId: { type: String, default: '' },
    secretId: { type: String, default: '' },
    defaultScope: { type: String as () => 'single' | 'global', default: 'global' },
    initialQuestion: { type: String, default: '' },
    activeAgent: { type: String as () => 'transcript' | 'global' | 'page-help', default: 'global' },
    pageContext: { type: Object as () => { path: string; name: string; pageType: string; label: string; supportsTranscriptContext: boolean } | null, default: null },
  },
  emits: ['close', 'openCitation'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const themeStore = useThemeStore();
    const { isDarkMode } = storeToRefs(themeStore);

    const question = ref(props.initialQuestion || '');
    const scope = ref<'single' | 'global'>(props.defaultScope);
    const agent = ref<'transcript' | 'global' | 'page-help'>(props.activeAgent as any);
    const loading = ref(false);
    const error = ref('');
    const answer = ref('');
    const citations = ref<Citation[]>([]);
    const noResults = ref(false);
    const history = ref<Array<{ role: 'user' | 'assistant'; content: string }>>([]);

    const supportsTranscript = computed(() => !!props.pageContext?.supportsTranscriptContext);
    const isPageHelp = computed(() => agent.value === 'page-help');
    const isTranscriptAgent = computed(() => agent.value === 'transcript');
    const effectiveAgent = computed(() => agent.value);
    const pageContextIcon = computed(() => {
      const pt = props.pageContext?.pageType;
      if (pt === 'transcript') return 'fas fa-file-lines';
      if (pt === 'video') return 'fas fa-video';
      if (pt === 'dashboard') return 'fas fa-table-columns';
      if (pt === 'orders') return 'fas fa-receipt';
      if (pt === 'team') return 'fas fa-users';
      if (pt === 'assets') return 'fas fa-photo-film';
      return 'fas fa-location-dot';
    });

    const switchAgent = (a: 'transcript' | 'global' | 'page-help') => {
      agent.value = a;
      if (a === 'transcript') scope.value = 'single';
      else if (a === 'global') scope.value = 'global';
      // page-help keeps scope global but is handled separately
      error.value = '';
    };

    watch(() => props.visible, (v) => {
      if (v) {
        if (props.initialQuestion) question.value = props.initialQuestion;
        agent.value = (props.activeAgent as any) || 'global';
        if (agent.value === 'page-help') {
          scope.value = 'global';
        } else if (!props.elementId || !supportsTranscript.value) scope.value = 'global';
        else scope.value = props.defaultScope;
        // If page doesn't support transcript, force away from single
        if (!supportsTranscript.value && scope.value === 'single') scope.value = 'global';
        if (agent.value === 'transcript' && (!props.elementId || !supportsTranscript.value)) {
          agent.value = 'global';
          scope.value = 'global';
        }
        error.value = '';
      }
    });

    watch(() => props.activeAgent, (a) => { if (a) agent.value = a as any; });
    watch(() => props.pageContext, () => {
      if (!supportsTranscript.value && scope.value === 'single') scope.value = 'global';
      if (!supportsTranscript.value && agent.value === 'transcript') agent.value = 'global';
    });

    watch(() => props.elementId, (id) => {
      if (!id) scope.value = 'global';
      if (!id && agent.value === 'transcript') agent.value = 'global';
    });

    const canAsk = computed(() => question.value.trim().length >= 3);

    const doAsk = async () => {
      if (!canAsk.value || loading.value) return;
      const q = question.value.trim();
      if (!isPageHelp.value && scope.value === 'single' && !props.elementId) {
        error.value = t('Select a transcript first');
        return;
      }
      if (!isPageHelp.value && scope.value === 'single' && !supportsTranscript.value) {
        error.value = t('This page has no transcript context');
        return;
      }
      loading.value = true;
      error.value = '';
      answer.value = '';
      citations.value = [];
      noResults.value = false;
      try {
        const res = await ask({
          question: q,
          scope: isPageHelp.value ? 'global' : scope.value,
          elementId: !isPageHelp.value && scope.value === 'single' ? props.elementId : undefined,
          projectId: props.projectId || undefined,
          outputId: props.outputId || undefined,
          secretId: props.secretId || undefined,
          conversationHistory: history.value.slice(-8),
          agent: agent.value,
          pageContext: props.pageContext ? { path: props.pageContext.path, name: props.pageContext.name, pageType: props.pageContext.pageType, label: props.pageContext.label } : undefined,
        });
        answer.value = res.answer;
        citations.value = res.citations || [];
        noResults.value = !!res.noResults;
        history.value.push({ role: 'user', content: q });
        history.value.push({ role: 'assistant', content: res.answer });
        question.value = '';
      } catch (e: any) {
        error.value = e?.response?.data?.message || e?.message || t('Failed to get answer');
      } finally {
        loading.value = false;
      }
    };

    const copyAnswer = async () => {
      if (!answer.value) return;
      try {
        await navigator.clipboard.writeText(answer.value);
        notify('success', t('Copied to clipboard'));
      } catch { notify('danger', t('Copy failed')); }
    };

    const openCitation = (c: Citation) => {
      emit('openCitation', c);
    };

    const applyPreset = (preset: AiPreset) => {
      const p = AI_PRESETS[preset];
      if (!p) return;
      question.value = p.question;
      void doAsk();
    };

    const clearHistory = () => {
      history.value = [];
      answer.value = '';
      citations.value = [];
      error.value = '';
      noResults.value = false;
    };

    return { isDarkMode, question, scope, agent, effectiveAgent, isPageHelp, isTranscriptAgent, supportsTranscript, pageContextIcon, switchAgent, loading, error, answer, citations, noResults, history, canAsk, doAsk, applyPreset, copyAnswer, openCitation, clearHistory, formatTime };
  },
});
</script>

<style scoped>
.ai-panel-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 9999; display: flex; justify-content: flex-end; }
.ai-panel-content { width: 560px; max-width: 100vw; height: 100vh; background: #fff; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; box-shadow: -8px 0 32px rgba(0,0,0,0.18); }
.ai-panel-content.dark { background: #1a2233; color: #e2e8f0; }
.slide-in-right { animation: slideIn 0.22s ease; }
@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
.ai-panel-header { display: flex; justify-content: space-between; align-items: center; }
.ai-panel-header h2 { font-size: 1.15rem; font-weight: 700; display: flex; gap: 8px; align-items: center; }
.ai-close { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: inherit; opacity: 0.7; }
.ai-close:hover { opacity: 1; }
.ai-section { display: flex; flex-direction: column; gap: 8px; }
.ai-label { font-weight: 600; font-size: 0.85rem; opacity: 0.9; }
.ai-hint { font-size: 0.8rem; opacity: 0.65; }
.ai-hint-warn { color: #d97706; }
.ai-textarea { width: 100%; border: 1px solid #e6e9f0; border-radius: 8px; padding: 10px 12px; font-size: 0.9rem; background: #f8fafc; color: #1f2937; resize: vertical; }
.dark .ai-textarea { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.ai-scope-toggle { display: flex; gap: 8px; }
.ai-scope-btn { flex: 1; padding: 10px 12px; border-radius: 8px; border: 1px solid #e6e9f0; background: #fff; font-size: 0.85rem; cursor: pointer; display: flex; gap: 6px; align-items: center; justify-content: center; }
.ai-scope-btn.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.ai-scope-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.dark .ai-scope-btn { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.dark .ai-scope-btn.active { background: #818cf8; color: #0f1420; }
.ai-source-chip { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f1f5f9; border-radius: 8px; font-size: 0.85rem; }
.dark .ai-source-chip { background: #212b40; }
.ai-source-name { font-weight: 600; }
.ai-conversation { display: flex; flex-direction: column; gap: 10px; max-height: 40vh; overflow-y: auto; padding: 4px; }
.ai-msg { padding: 10px 12px; border-radius: 10px; font-size: 0.88rem; line-height: 1.5; }
.ai-msg.user { background: #eef2ff; border: 1px solid #c7d2fe; align-self: flex-end; max-width: 85%; }
.ai-msg.assistant { background: #f8fafc; border: 1px solid #e6e9f0; }
.dark .ai-msg.user { background: #312e81; border-color: #4338ca; }
.dark .ai-msg.assistant { background: #212b40; border-color: #2a3550; }
.ai-msg-role { font-weight: 700; font-size: 0.75rem; opacity: 0.7; margin-bottom: 4px; display: flex; gap: 6px; align-items: center; }
.ai-msg-body { white-space: pre-wrap; }
.ai-result { background: #f8fafc; border: 1px solid #e6e9f0; border-radius: 10px; padding: 14px; }
.dark .ai-result { background: #212b40; border-color: #2a3550; }
.ai-result-header { display: flex; justify-content: space-between; align-items: center; }
.ai-result-body { white-space: pre-wrap; line-height: 1.6; font-size: 0.9rem; }
.ai-citations { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.ai-citations-title { font-weight: 600; font-size: 0.8rem; opacity: 0.8; display: flex; gap: 6px; align-items: center; }
.ai-citation { text-align: left; padding: 8px 10px; border-radius: 8px; border: 1px solid #e6e9f0; background: #fff; cursor: pointer; display: flex; flex-direction: column; gap: 2px; }
.ai-citation:hover { border-color: #4f46e5; background: #eef2ff; }
.dark .ai-citation { background: #1a2233; border-color: #2a3550; color: #e2e8f0; }
.dark .ai-citation:hover { border-color: #818cf8; background: #1e293b; }
.ai-citation-file { font-weight: 600; font-size: 0.8rem; }
.ai-citation-time { font-size: 0.75rem; opacity: 0.6; }
.ai-citation-snippet { font-size: 0.78rem; opacity: 0.75; font-style: italic; }
.ai-context-banner { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; font-size: 0.8rem; background: #f1f5f9; border: 1px solid #e6e9f0; }
.dark .ai-context-banner { background: #212b40; border-color: #2a3550; }
.ai-context-label { opacity: 0.85; }
.ai-context-badge { margin-left: auto; font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; background: #e0e7ff; color: #4338ca; font-weight: 600; }
.ai-context-badge.ok { background: #dcfce7; color: #166534; }
.ai-context-badge.warn { background: #fef3c7; color: #92400e; }
.dark .ai-context-badge { background: #312e81; color: #c7d2fe; }
.dark .ai-context-badge.ok { background: #14532d; color: #bbf7d0; }
.dark .ai-context-badge.warn { background: #78350f; color: #fde68a; }
.ai-agent-tabs { display: flex; gap: 6px; }
.ai-agent-tab { flex: 1; padding: 8px 10px; border-radius: 8px; border: 1px solid #e6e9f0; background: #fff; font-size: 0.8rem; cursor: pointer; display: flex; gap: 6px; align-items: center; justify-content: center; }
.ai-agent-tab.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.ai-agent-tab:disabled { opacity: 0.45; cursor: not-allowed; }
.dark .ai-agent-tab { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.dark .ai-agent-tab.active { background: #818cf8; color: #0f1420; }
.ai-status { padding: 10px 12px; border-radius: 8px; font-size: 0.85rem; }
.ai-status-error { background: rgba(239,68,68,0.12); color: #dc2626; border-left: 4px solid #dc2626; }
.ai-status-progress { background: rgba(79,70,229,0.08); color: #4f46e5; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.ai-question-row { gap: 8px; }
.ai-question-actions { display: flex; justify-content: space-between; align-items: center; }
.ai-preset-row { display: flex; flex-direction: column; gap: 6px; }
.ai-preset-label { font-size: 0.75rem; font-weight: 600; opacity: 0.7; }
.ai-presets-row { display: flex; flex-wrap: wrap; gap: 6px; }
.ai-chip { padding: 6px 12px; border-radius: 999px; border: 1px solid #e6e9f0; background: #fff; font-size: 0.8rem; cursor: pointer; }
.ai-chip:disabled { opacity: 0.45; cursor: not-allowed; }
.ai-chip.active, .ai-preset-chip:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.dark .ai-chip { background: #212b40; border-color: #2a3550; color: #e2e8f0; }
.dark .ai-chip.active, .dark .ai-preset-chip:hover { background: #818cf8; color: #0f1420; }
.ai-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: auto; padding-top: 12px; border-top: 1px solid #e6e9f0; }
.dark .ai-actions { border-color: #2a3550; }
</style>
